import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { Button, Card, Input, Notice, fmtDateTime, formatUsd } from '@rydres/ui';
import { driverApi } from '../lib/api.js';
import { useProfile } from '../lib/profile.js';
import type { ClosedReferral, IncomingReferral } from '../lib/types.js';

const CLASH_TEXT = {
  booked: 'You already have a confirmed ride at this time.',
  hours: 'This pickup is outside your hours.',
  block: 'You marked this time off, or your phone calendar shows it busy.',
  accepted: 'You have sent a price for another ride at this time. The first rider to answer gets the time.',
} as const;

/** A row in Bookings and on Today for a ride another driver referred to this one. */
export function ReferralRow({ r, tz }: { r: IncomingReferral; tz?: string }) {
  return (
    <Link to={`/referrals/${r.id}`} className="block rounded-2xl border border-accent bg-card p-4">
      <div className="text-sm font-bold">{fmtDateTime(r.startsAt, tz)} · {r.passengers} pax</div>
      <div className="truncate text-xs text-muted">{r.pickupAddress} → {r.dropoffAddress}</div>
      <div className="mt-1 text-xs font-bold text-accent-text">{r.status === 'waiting_rider' ? `Waiting for the rider · your price ${formatUsd(r.priceCents ?? 0)}` : `Referred by ${r.fromDriver.displayName ?? r.fromDriver.legalName}`}</div>
    </Link>
  );
}

/** A ride another driver referred: the trip, never the rider, and the driver's own price or a no. */
export function ReferralScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [r, setR] = useState<IncomingReferral | ClosedReferral | null>(null);
  const [price, setPrice] = useState('');
  const [hours, setHours] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    driverApi.referral(id).then(setR, () => navigate('/bookings?view=requests'));
  }, [id, navigate]);
  if (!r) return <p className="text-sm text-muted">Loading…</p>;
  if ('closed' in r) {
    if (r.newBookingId) return <Navigate to={`/bookings/${r.newBookingId}`} replace />;
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-black">Referred ride</h1>
        <Notice tone="info">This referred ride has closed. Nothing more is needed from you.</Notice>
        <Link to="/bookings?view=requests" className="block rounded-xl bg-card2 px-4 py-3 text-center text-sm font-bold">Back to requests</Link>
      </div>
    );
  }
  const tz = profile?.timezone;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-full bg-card2 p-2" aria-label="Back">‹</button>
        <h1 className="text-xl font-black">{fmtDateTime(r.startsAt, tz)}</h1>
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      <Notice tone="info">
        {r.status === 'waiting_rider'
          ? `The rider is deciding, until ${fmtDateTime(r.expiresAt, tz)}. You see their name and number if they accept you, and the ride is yours once they pay or agree.`
          : `${r.fromDriver.displayName ?? r.fromDriver.legalName} cannot take this ride. Name your own price and the rider decides. No money or fee passes between you. Open until ${fmtDateTime(r.expiresAt, tz)}.`}
      </Notice>
      {r.clash && <Notice tone="warn">{CLASH_TEXT[r.clash]}</Notice>}
      <Card>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">From</dt><dd>{r.pickupAddress}</dd>
          <dt className="text-muted">To</dt><dd>{r.dropoffAddress}</dd>
          <dt className="text-muted">Passengers</dt><dd>{r.passengers}</dd>
          {r.notes && <><dt className="text-muted">Instructions</dt><dd>{r.notes}</dd></>}
          {r.priceCents != null && <><dt className="text-muted">Your price</dt><dd className="font-bold">{formatUsd(r.priceCents)}</dd></>}
        </dl>
      </Card>
      {r.status === 'waiting_driver' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Price $ (per vehicle)</span><Input type="number" step="1" min="1" value={price} onChange={(e) => setPrice(e.target.value)} /></label>
            <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Trip length (hours)</span><Input type="number" step="0.5" min="0.5" placeholder="1" value={hours} onChange={(e) => setHours(e.target.value)} /></label>
          </div>
          <Button className="w-full" disabled={busy || !(Number(price) >= 1)} onClick={() => void run(async () => setR(await driverApi.acceptReferral(id, { priceCents: Math.round(Number(price) * 100), ...(hours ? { durationMin: Math.round(Number(hours) * 60) } : {}) })))}>Accept at this price</Button>
          <Button variant="danger" className="w-full" disabled={busy} onClick={() => void run(async () => { await driverApi.declineReferral(id); navigate('/bookings?view=requests'); })}>Decline</Button>
        </div>
      )}
    </div>
  );
}
